import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { services, stats, companyDetails } from '../lib/mock';
import SEO from '../components/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const faqs = [
  {
    q: "What is a water treatment plant and how does it work?",
    a: "A water treatment plant is an engineered system that removes contaminants, chemicals and impurities from water or wastewater. BG TECHNO CHEM designs STP, ETP, WTP and RO based plants that use physical, biological and chemical processes to make water safe for reuse, discharge or industrial process needs."
  },
  {
    q: "Who is the best water treatment plant manufacturer in Ludhiana, Punjab?",
    a: "BG TECHNO CHEM is a trusted, ISO 9001:2015 & ISO 14001:2015 certified water treatment plant manufacturer based in Ludhiana, Punjab with 28+ years of experience delivering 500+ projects across India for industrial and commercial clients."
  },
  {
    q: "What is the difference between an STP and an ETP plant?",
    a: "A Sewage Treatment Plant (STP) treats domestic wastewater from toilets, kitchens and bathrooms, while an Effluent Treatment Plant (ETP) treats complex industrial effluent containing chemicals, heavy metals and high COD/BOD loads. We manufacture and install both as per Pollution Control Board norms."
  },
  {
    q: "Do you supply RO plants and water treatment chemicals?",
    a: "Yes. We supply domestic, commercial and industrial RO systems, DM plants, softening plants, cooling towers, boilers as well as a complete range of water treatment chemicals including antiscalants, biocides, oxygen scavengers and descaling chemicals."
  },
  {
    q: "How much does a water treatment plant cost?",
    a: "The cost of a water treatment plant depends on the daily capacity (KLD), water quality and technology required. Contact BG TECHNO CHEM for a free site assessment and a customized, competitive quote for your STP, ETP or RO plant."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a }
  }))
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "BG TECHNO CHEM",
  "url": "https://bgtechnochem.com/",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://bgtechnochem.com/products?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export default function Home() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Water Treatment Plant Manufacturer in Ludhiana"
        description="BG TECHNO CHEM is a leading water treatment plant manufacturer in Ludhiana, Punjab. STP, ETP, WTP, industrial RO systems & water treatment chemicals. ISO certified, 28+ years experience, 500+ projects."
        keywords="water treatment plant, water treatment plant manufacturer, STP plant manufacturer, ETP plant Ludhiana, industrial RO system, water treatment chemicals Punjab, sewage treatment plant, effluent treatment plant"
        schema={[faqSchema, websiteSchema]}
      />
      
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url("https://images.unsplash.com/photo-1533077162801-86490c593afb")',
          }}
        >
          <div className="absolute inset-0 bg-slate-900/70 mix-blend-multiply" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-6 drop-shadow-lg">
            Industrial Water Treatment Plants for a <br />
            <span className="text-emerald-400">Sustainable Future</span>
          </h1>
          <p className="mt-4 text-xl text-slate-200 max-w-3xl mx-auto mb-10 font-light">
            Leading water treatment plant manufacturer in Ludhiana, Punjab. {companyDetails.tagline} — trusted by industries for efficient STP, ETP, WTP & RO solutions.
          </p>
          <div className="flex justify-center gap-4 flex-col sm:flex-row">
             <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-brand-darkBlue text-white min-w-[180px] h-14 text-lg">
                Get a Quote
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20 min-w-[180px] h-14 text-lg backdrop-blur-sm">
                Explore Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-primary py-12 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="space-y-2">
                <p className="text-4xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-blue-100 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl mb-4">Our Expertise</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive water and wastewater treatment solutions tailored for industrial efficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 4).map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-t-4 border-t-transparent hover:border-t-primary">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                      <Icon className="w-6 h-6 text-primary group-hover:text-white" />
                    </div>
                    <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {service.shortDesc}
                    </p>
                    <Link to="/services" className="inline-flex items-center text-primary font-semibold hover:underline">
                      Learn more <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-8 -right-4 w-72 h-72 bg-emerald-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              <img 
                src="https://images.unsplash.com/photo-1705708551758-76b153fa536e" 
                alt="Industrial Facility" 
                className="relative rounded-2xl shadow-2xl z-10 w-full h-auto object-cover"
              />
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl mb-6">
                Why Industry Leaders Trust <span className="text-primary">BG TECHNO CHEM</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                With over two decades of dedicated service, we don't just treat water; we engineer solutions that improve your operational efficiency and environmental compliance.
              </p>
              
              <div className="space-y-6">
                {[
                  "Custom-engineered solutions for specific industry needs",
                  "24/7 Operational maintenance and support",
                  "Global quality standards and compliance",
                  "Advanced eco-friendly technologies"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="ml-4 text-lg text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button size="lg" className="bg-slate-900 text-white hover:bg-slate-800">
                    About Our Journey
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section (SEO rich snippets) */}
      <section className="py-24 bg-slate-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">
              Everything you need to know about our water treatment plants and services.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} data-testid={`faq-item-${idx}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-slate-900">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Optimize Your Water Management?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a customized proposal for your industrial water treatment needs today.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-primary hover:bg-blue-50 text-lg px-8 h-14 font-bold shadow-lg">
              Contact Us Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
